import { IStringInputSchema } from '../types'
import { useAppSelector } from '../../../hooks/useAppSelector'
import { inputValidationCheck } from '../../../validators/inputValidators/inputValidators'
import { useFormValidation } from '../../../hooks/useFormValidation'
import { getLabel } from '../../../validators'
import { stringInputErrors } from '../../../utils/validationErrors/stringInputErrors'
import React, { memo, useEffect, useState } from 'react'
import { initInputData } from '../../../utils/InitInputData'
import { inputDataDispatcher } from '../../../utils/InputDataDispatcher'
import { buildControlsExt, textInput } from '../../inputs/controls'
import { useAppDispatch } from '../../../hooks/useAppDispatch'
import { PATH_JOINER } from '../utils/getConfigBitPath'
import { selectConfigBit, selectConfigIsChecked } from '../../../store/selectors/configSelectors'

interface TextProps {
    dataPath: string
    pathWithElementID: string | undefined
    dataSchema: IStringInputSchema
    name: string
    wrapper?: string
}

export const Text = memo(function Text(props: TextProps) {
    const { dataPath, dataSchema, name, wrapper, pathWithElementID } = props

    const dispatch = useAppDispatch()
    const configBit = useAppSelector(
        selectConfigBit(dataPath.split(PATH_JOINER))
    )
    const path = [...dataPath.split(PATH_JOINER), name]
    const [checkPath, setCheckPath] = useState<string[]>([])

    const isChecked= useAppSelector(selectConfigIsChecked)

    const {
        is_required: isRequired,
        name: inputLabel,
        type,
        description: tooltipText,
        is_email: isEmail,
        default: defaultValue,
    } = dataSchema

    const validationCheck = inputValidationCheck(type, dataSchema)
    const [
        inputState,
        setInputState,
        touched,
        setTouched,
        validationResult,
        isValid,
    ] = useFormValidation(configBit || {}, { [name]: validationCheck })
    const [guid, , tabName] = path
    const label = inputLabel || getLabel(name)
    const className = !wrapper ? 'w-50' : ''
    const errorKey = pathWithElementID
        ? [...pathWithElementID.split(PATH_JOINER), name].join(PATH_JOINER)
        : path.join(PATH_JOINER)
    const isUndefined: boolean = typeof configBit?.[name] === 'undefined'
    const isDataEmpty: number = Object.keys(configBit || {}).length
    const isEmpty: boolean = !configBit || !isDataEmpty || isUndefined
    const errMsg = stringInputErrors(inputState?.[name], dataSchema)

    useEffect(() => {
        if (dataPath.length) {
            setCheckPath(dataPath.split(PATH_JOINER))
            initInputData(
                {
                    isEmpty,
                    name,
                    path,
                    dataSchema,
                    isRequired,
                    guid,
                    errorKey,
                    data: configBit,
                    setInputState,
                    tabName,
                },
                dispatch
            )
        }
    }, []) // eslint-disable-line

    useEffect(() => {
        if (isChecked[guid] && dataPath.length) {
            isValid()
        }
    }, [isChecked, dataPath.length]) // eslint-disable-line

    useEffect(() => {
        if (configBit && dataPath.length) {
            setInputState(configBit)
        }
    }, [configBit, dataPath.length]) // eslint-disable-line

    useEffect(() => {
        if (dataPath.length) {
            inputDataDispatcher(
                {
                    isEmpty,
                    name,
                    path,
                    dataSchema,
                    data: configBit,
                    inputState,
                    validationCheck,
                    guid,
                    errorKey,
                    isUndefined,
                    checkPath,
                    type,
                },
                dispatch
            )
        }
    }, [touched[name], isChecked[guid]]) // eslint-disable-line

    const handleOnBlur = () => {
        inputDataDispatcher(
            {
                isEmpty,
                name,
                path,
                dataSchema,
                data: configBit,
                inputState,
                validationCheck,
                guid,
                errorKey,
                isUndefined,
                checkPath,
                type,
            },
            dispatch
        )
        setTouched((prev: any) => ({ ...prev, [name]: true }))
    }

    const controlDescriptors = [
        textInput({
            name,
            tooltipText,
            label,
            className,
            isColumn: true,
            errMsg,
            isRequired,
            isEmail,
            placeholder: defaultValue,
            onBlur: handleOnBlur,
        }),
    ]

    const textArea = buildControlsExt(
        controlDescriptors,
        inputState,
        setInputState,
        touched,
        setTouched,
        validationResult
    )

    return <div className={`${!wrapper ? 'w-100' : ''}`}>{textArea}</div>
})
